import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import MilitaryTechRoundedIcon from '@mui/icons-material/MilitaryTechRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import { useAuth } from '../../AuthContext';
import Pot from './../../components/Pot'
import PieChart from './../../charts/pie'
import VotePlayer from "../../components/VotePlayer";
import AppLayout from "../../components/ui/AppLayout";
import PageHeader from "../../components/ui/PageHeader";
import SectionCard from "../../components/ui/SectionCard";
import StatCard from "../../components/ui/StatCard";
import EmptyState from "../../components/ui/EmptyState";
import MyPayments from "../Payments/MyPayments";
import api from '../../api';

const Dashboard = () => {
  const { isAuthenticated, userLogado } = useAuth();
  const navigate = useNavigate();

  const [qtdVotos, setQtdVotos] = useState([]);
  const [qtdVotosUserLogado, setQtdVotosUserLogado] = useState([]);
  const [qtdUserForTorre, setQtdUserForTorre] = useState([]);
  const [raking, setRanking] = useState(0);
  const [myPote, setMyPote] = useState('');
  const [renderPieVotoUsers, setRenderPieVotoUsers] = useState(false);
  const [renderMyPote, setRenderMypote] = useState(false);
  const [potes, setPotes] = useState(null);
  const [listUserRaking, setListUserRaking] = useState([]);
  const [peladaAberta, setPeladaAberta] = useState(false);
  const [torneios, setTorneios] = useState([]);
  const [torneioSelecionado, setTorneioSelecionado] = useState('');

  const getQtdVotos = async () => {
    try {
      const response = await api.get('report/votos', { params: { torneioId: torneioSelecionado } });
      setQtdVotos(response.data)
    } catch (error) {
      console.error('Erro ao obter votos:', error);
    }
  };

  const getTorneios = async () => {
    try {
      const response = await api.get('getTorneios');
      setTorneios(response.data)
      if (!torneioSelecionado && response.data.length > 0) {
        const ultimo = response.data[response.data.length - 1];
        setTorneioSelecionado(ultimo._id);
      }
    } catch (error) {
      console.error('Erro ao obter Torneios:', error);
    }
  };

  const getQtdVotosUserLogado = async () => {
    try {
      const response = await api.get('report/qtdvotosforportsuser', { params: { torneioId: torneioSelecionado } });
      setQtdVotosUserLogado(response.data)
      setRenderPieVotoUsers(true);
    } catch (error) {
      setRenderPieVotoUsers(false);
    }
  };

  const getMyPote = async () => {
    try {
      const response = await api.post('user/ranking', { torneio_id: torneioSelecionado });
      if (response.status === 200 && response.data) {
        setRanking(response.data.ranking.ranking);
        setMyPote(response.data.ranking.myPote)
        setRenderMypote(true)
      } else {
        setRenderMypote(false)
      }
    } catch (error) {
      setRenderMypote(false)
      setRanking(0);
    }
  };

  const getUserForTorre = async () => {
    try {
      const response = await api.get('report/getQtdForTorre');
      if (response.data) setQtdUserForTorre(response.data)
    } catch (error) {
      console.error('Erro obter user for torre', error);
    }
  }

  const getUsersRakingTotal = async () => {
    try {
      const response = await api.get('report/getPotesforPeso', { params: { torneioId: torneioSelecionado } });
      if (response.data) {
        setListUserRaking(response.data.map((item, index) => ({ ...item, posicao: index + 1, valorTotal: Number(item.valorTotal).toFixed(2) })))
      }
    } catch (error) {
      setListUserRaking([]);
    }
  };

  const getPotes = async () => {
    try {
      const response = await api.post('/getPotesFinalTorneio', { torneio: torneioSelecionado });
      if (response.data) setPotes(response.data)
    } catch (error) {
      setPotes(null);
    }
  };

  const getPeladaActive = async () => {
    try {
      const response = await api.get('/getPeladaActive');
      setPeladaAberta(Boolean(response.data.data));
    } catch (error) {
      console.error('Erro obter pelada ativa', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      getTorneios();
      getPeladaActive();
      getUserForTorre();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastTorneio = useRef(null);
  useEffect(() => {
    if (isAuthenticated && torneioSelecionado && lastTorneio.current !== torneioSelecionado) {
      lastTorneio.current = torneioSelecionado;
      getQtdVotos();
      getQtdVotosUserLogado();
      getMyPote();
      getPotes();
      getUsersRakingTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torneioSelecionado]);

  const totalVotos = Array.isArray(qtdVotos) ? qtdVotos[0] : undefined;
  const totalUsuarios = Array.isArray(qtdUserForTorre) ? qtdUserForTorre.reduce((t, v) => t + (Number(v) || 0), 0) : undefined;

  const dataPie = {
    labels: ['Votos realizados', 'Não realizados'],
    datasets: [{ label: 'Número de votos', data: qtdVotos }],
  };

  const dataPieTorre = {
    labels: ['Natura', 'Acqua'],
    datasets: [{ label: 'Usuários', data: qtdUserForTorre }],
  };

  const dataPieVotosUserLogado = {
    labels: ['Pote 1', 'Pote 2', 'Pote 3', 'Pote 4', 'Pote 5', 'Pote 6'],
    datasets: [{ label: 'Votos', data: qtdVotosUserLogado }],
  };

  const columns = [
    { field: 'posicao', headerName: '#', width: 70 },
    { field: 'jogador', headerName: 'Jogador', flex: 1, minWidth: 180 },
    { field: 'valorTotal', headerName: 'Pontos', width: 140 },
  ];

  const hasPotes = potes && Object.values(potes).some((p) => p.length > 0);

  return (
    <AppLayout>
      <PageHeader
        eyebrow="Torneio"
        title="Painel do torneio"
        subtitle={`Votação dos potes, seu ranking e seu pote, ${userLogado?.nickName || (userLogado?.nome || '').split(' ')[0] || 'jogador'}.`}
        actions={
          <TextField select size="small" label="Torneio" value={torneioSelecionado} onChange={(e) => setTorneioSelecionado(e.target.value)} sx={{ minWidth: 220 }}>
            {torneios.map((torneio) => (
              <MenuItem key={torneio._id} value={torneio._id}>{torneio.nome}</MenuItem>
            ))}
          </TextField>
        }
      />

      {peladaAberta && (
        <Alert
          severity="info"
          icon={<PlaceRoundedIcon />}
          sx={{ mb: 3, alignItems: 'center' }}
          action={<Button variant="contained" size="small" onClick={() => navigate('/ordem-chegada')}>Confirmação / Ordem de chegada</Button>}
        >
          Existe uma pelada aberta. Registre sua chegada quando estiver na concentração.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Meu ranking" value={renderMyPote ? `${raking}º` : '—'} hint={renderMyPote ? 'Pela votação dos potes' : 'Vote para liberar'} icon={<MilitaryTechRoundedIcon />} tone="gold" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Meu pote" value={renderMyPote ? myPote.replace('pote', '') : '—'} hint={renderMyPote ? 'Pote atual' : 'Vote para liberar'} icon={<Inventory2RoundedIcon />} tone="brand" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Votos realizados" value={totalVotos ?? '—'} icon={<HowToVoteRoundedIcon />} tone="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Usuários cadastrados" value={totalUsuarios || '—'} icon={<ApartmentRoundedIcon />} tone="info" />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <MyPayments sx={{ height: '100%' }} />
        </Grid>
        <Grid item xs={12} md={5}>
          <SectionCard title="Votos a meu favor" subtitle="Em quais potes você foi colocado" sx={{ height: '100%' }}>
            {renderPieVotoUsers ? (
              <Box sx={{ maxWidth: 300, mx: 'auto' }}><PieChart data={dataPieVotosUserLogado} /></Box>
            ) : (
              <EmptyState icon={<HowToVoteRoundedIcon />} title="Realize seu voto" description="Depois de votar você vê como os outros te avaliaram." action={<Button variant="contained" onClick={() => navigate('/votacao')}>Votar agora</Button>} />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} sm={6}>
          <SectionCard title="Participação na votação">
            <Box sx={{ maxWidth: 280, mx: 'auto' }}><PieChart data={dataPie} /></Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} sm={6}>
          <SectionCard title="Jogadores por torre">
            <Box sx={{ maxWidth: 280, mx: 'auto' }}><PieChart data={dataPieTorre} /></Box>
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard title="Potes formados" subtitle="Resultado ponderado da votação" sx={{ mb: 3 }}>
        {hasPotes ? (
          <Grid container spacing={2}>
            {Object.keys(potes).map((potNum) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={potNum}>
                <Pot potNum={potNum} playersInPot={potes[potNum]} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <EmptyState title="Potes ainda não formados" description="Os potes são liberados após o prazo da votação." />
        )}
      </SectionCard>

      {listUserRaking.length > 0 && (
        <SectionCard title="Ranking dos jogadores" subtitle="Pontuação ponderada por pote" sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pesos: pote 1 = 1,72 · pote 2 = 1,53 · pote 3 = 1,43 · pote 4 = 1,33 · pote 5 = 1,22 · pote 6 = 1,13
          </Typography>
          <DataGrid
            rows={listUserRaking}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
            pageSizeOptions={[10, 36]}
          />
        </SectionCard>
      )}

      <Stack>
        <VotePlayer torneioId={torneioSelecionado} />
      </Stack>
    </AppLayout>
  );
};

export default Dashboard;
